import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { AsyncOperationsHubUrl } from '../../config/config';
import { authService } from '../AuthService/AuthService';
import { getRetryDelay, getTopicKey, isSupportedSchema } from './helpers';
import {
  IApiEnvelope,
  IAsyncOperationsHubListener,
  IAsyncOperationsSnapshot,
  IAsyncOperationState,
  ITopicDataChanged,
  ITopicTarget,
  TRealtimeConnectionStatus,
} from './types';

type TFollowedTarget = { target: ITopicTarget; refCount: number };

/**
 * The tab's one connection to the async-operations hub, shared by every page that follows
 * operations. It starts with the first page that subscribes, lives as long as the tab and
 * subscribes the followed pages again after every reconnect.
 */
class AsyncOperationsHub {
  private connection: HubConnection | null = null;
  private listener: IAsyncOperationsHubListener | null = null;
  private readonly targets = new Map<string, TFollowedTarget>();
  /** Pages the server refused to follow: not asked again until the connection restarts */
  private readonly refused = new Set<string>();
  private status: TRealtimeConnectionStatus = 'Disconnected';
  /** Bumped by every restart, so that a start attempt of the previous connection ends quietly */
  private generation = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Grows with every close the server makes without a reconnect, so that such closes back off */
  private closeRetryCount = 0;
  private isStopping = false;

  setListener(listener: IAsyncOperationsHubListener): void {
    this.listener = listener;
  }

  getStatus(): TRealtimeConnectionStatus {
    return this.status;
  }

  subscribe(target: ITopicTarget): void {
    const followed = this.targets.get(getTopicKey(target));
    if (followed) {
      followed.refCount += 1;
      return;
    }
    this.targets.set(getTopicKey(target), { target, refCount: 1 });
    if (this.status === 'Connected') this.invokeSubscribe(target);
    this.ensureStarted();
  }

  unsubscribe(target: ITopicTarget): void {
    const key = getTopicKey(target);
    const followed = this.targets.get(key);
    if (!followed) return;
    followed.refCount -= 1;
    if (followed.refCount > 0) return;
    this.targets.delete(key);
    this.refused.delete(key);
    if (this.status === 'Connected') {
      this.connection
        ?.invoke('Unsubscribe', target)
        .catch(err => console.warn('Unsubscribe failed', err));
    }
  }

  /** Asks for a fresh snapshot of a followed page, to heal an update that got lost on the way. */
  resync(target: ITopicTarget): void {
    if (this.status === 'Connected' && this.targets.has(getTopicKey(target))) {
      this.invokeSubscribe(target);
    }
  }

  /** What a connection may follow is fixed by its token: restart it when the token changes. */
  async restart(): Promise<void> {
    if (!this.connection) return;
    this.generation += 1;
    this.clearRetry();
    await this.stopConnection();
    this.refused.clear();
    this.listener?.onRestarted();
    if (this.targets.size) this.start(0);
  }

  private ensureStarted(): void {
    if (!this.connection) this.connection = this.createConnection();
    if (!this.retryTimer) this.start(0);
  }

  private createConnection(): HubConnection {
    const connection = new HubConnectionBuilder()
      .withUrl(AsyncOperationsHubUrl, {
        // the server serves no negotiation to browsers, so the WebSocket request is the only one
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
        accessTokenFactory: () => authService.getFreshAdminToken(),
      })
      .withAutomaticReconnect({
        // never give up while the tab is open
        nextRetryDelayInMilliseconds: ({ previousRetryCount }) => getRetryDelay(previousRetryCount),
      })
      .configureLogging(LogLevel.Warning)
      .build();

    // registered before the start, so that no message is missed
    connection.on('AsyncOperationUpdated', (state: IAsyncOperationState) => {
      if (isSupportedSchema(state)) this.listener?.onOperationUpdated(state);
      else console.warn('Unsupported AsyncOperationUpdated schema', state?.schemaVersion);
    });
    connection.on('TopicDataChanged', (change: ITopicDataChanged) => {
      if (isSupportedSchema(change)) this.listener?.onTopicDataChanged(change);
      else console.warn('Unsupported TopicDataChanged schema', change?.schemaVersion);
    });
    connection.onreconnecting(() => this.setStatus('Reconnecting'));
    connection.onreconnected(() => {
      // a new connection belongs to no group
      this.setStatus('Connected');
      this.subscribeAll();
    });
    connection.onclose(() => {
      this.setStatus('Disconnected');
      // the server closed it without a reconnect: start over
      if (!this.isStopping && this.targets.size) this.scheduleStart(this.closeRetryCount++);
    });
    return connection;
  }

  /** The automatic reconnect covers a dropped connection only: a failed start is retried here. */
  private start(retryCount: number): void {
    const { connection, generation } = this;
    if (!connection || connection.state !== HubConnectionState.Disconnected) return;
    this.setStatus('Connecting');
    connection
      .start()
      .then(() => {
        if (generation !== this.generation) return;
        this.setStatus('Connected');
        this.subscribeAll();
      })
      .catch(() => {
        if (generation !== this.generation) return;
        this.setStatus('Disconnected');
        // when no page follows operations any more, the next subscribe starts again
        if (this.targets.size) this.scheduleStart(retryCount + 1);
      });
  }

  private scheduleStart(retryCount: number): void {
    this.clearRetry();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      // when no page follows operations any more, the next subscribe starts again
      if (this.targets.size) this.start(retryCount);
    }, getRetryDelay(retryCount));
  }

  private async stopConnection(): Promise<void> {
    this.isStopping = true;
    try {
      // runs the onclose callbacks before it resolves
      await this.connection?.stop();
    } catch (err) {
      console.warn('Stopping the hub connection failed', err);
    } finally {
      this.isStopping = false;
    }
    this.setStatus('Disconnected');
  }

  private subscribeAll(): void {
    this.targets.forEach(({ target }) => this.invokeSubscribe(target));
  }

  private invokeSubscribe(target: ITopicTarget): void {
    const key = getTopicKey(target);
    if (!this.connection || this.refused.has(key)) return;
    this.connection
      .invoke<IApiEnvelope<IAsyncOperationsSnapshot>>('Subscribe', target)
      .then(response => {
        // the page may have left while the call was on its way
        if (!this.targets.has(key)) return;
        if (response?.isSuccess && response.data) {
          this.closeRetryCount = 0;
          this.listener?.onSnapshot(response.data);
          return;
        }
        this.refuse(target, String(response?.error?.errorCode ?? 'Unknown'));
      })
      // the connection dropped during the call: the reconnect subscribes again
      .catch(err => console.warn('Subscribe failed', err));
  }

  /** A refused page is not asked again: the server answers the same until the token changes. */
  private refuse(target: ITopicTarget, errorCode: string): void {
    if (errorCode === 'InvalidData') {
      console.error('The hub connection follows too many pages: an Unsubscribe is missing');
    }
    this.refused.add(getTopicKey(target));
    this.listener?.onSubscriptionRefused(target, errorCode);
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private setStatus(status: TRealtimeConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.listener?.onStatusChanged(status);
  }
}

export const asyncOperationsHub = new AsyncOperationsHub();
